/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/JobQaParticipants.tsx
 * FILE_ID: JOBHUB-QA-PARTICIPANTS-001
 * PURPOSE: List and add job participants inside Quote Details.
 */
import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  QaParticipantsEditor,
  type QaParticipantPerson,
  type QaParticipantRow,
} from '../../qa/QaParticipantsEditor'
import { useJobHubDataAdapter } from '../../jobHubDataAdapter'
import { qaErrorMessage } from './qaTabSelectors'
import type { CoverPerson } from './useInspectionRecordSettings'

function rowsOf(data: unknown): Array<Record<string, unknown>> {
  if (Array.isArray(data)) return data as Array<Record<string, unknown>>
  const results = (data as { results?: unknown[] } | null)?.results
  return Array.isArray(results) ? results as Array<Record<string, unknown>> : []
}

function text(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

export function JobQaParticipants({
  jobId,
  onError,
  people,
  onChangePeople,
}: {
  jobId: number
  onError: (message: string | null) => void
  people?: CoverPerson[]
  onChangePeople?: (next: CoverPerson[]) => Promise<void>
}) {
  const snapshot = Boolean(onChangePeople)
  const { client } = useJobHubDataAdapter()
  const queryClient = useQueryClient()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { data } = useQuery({
    queryKey: ['qa-participants', String(jobId)],
    enabled: !snapshot,
    queryFn: () => client.get('/api/qa/participants/', { params: { job_id: jobId } }).then((response) => response.data),
  })
  const { data: team } = useQuery({
    queryKey: ['job-team', jobId],
    enabled: !snapshot,
    queryFn: () => client.get(`/jobs/${jobId}/team`).then((response) => response.data),
  })
  const { data: exclusions } = useQuery({
    queryKey: ['qa-team-exclusions', jobId],
    enabled: !snapshot,
    queryFn: () => client.get('/api/qa/team-exclusions/', { params: { job_id: jobId } }).then((response) => response.data),
  })
  const excluded = new Set(
    Array.isArray(exclusions) ? exclusions.filter((id): id is number => typeof id === 'number') : [],
  )
  const participants: QaParticipantRow[] = rowsOf(data).map((row, index) => ({
    key: text(row.id) || `${text(row.user_email_display)}-${index}`,
    name: text(row.user_name),
    email: text(row.user_email_display),
    role: text(row.role_on_job),
    kind: 'participant' as const,
    userId: typeof row.user === 'number' ? row.user : typeof row.user_id === 'number' ? row.user_id : undefined,
  }))
  const participantIds = new Set(participants.map((row) => row.userId).filter((id): id is number => id != null))
  const teamRows = ((team as { team?: Array<Record<string, unknown>> } | undefined)?.team ?? [])
    .filter((row) => row.is_active !== false)
    .map((row): QaParticipantRow | null => {
      const userId = typeof row.user_id === 'number' ? row.user_id : undefined
      if (userId == null || participantIds.has(userId) || excluded.has(userId)) return null
      return {
        key: `team-${userId}`,
        name: text(row.name) || text(row.user_name),
        email: text(row.email) || text(row.user_email_display),
        role: 'Job team',
        kind: 'team',
        userId,
      }
    })
    .filter((row): row is QaParticipantRow => row != null)
  const snapshotRows: QaParticipantRow[] = (people ?? []).map((row, index) => ({
    key: row.email || `${row.name}-${index}`,
    name: row.name,
    email: row.email,
    role: row.role,
    kind: 'participant' as const,
    userId: row.userId,
  }))
  const rows = snapshot ? snapshotRows : [...participants, ...teamRows]
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['qa-participants', String(jobId)] })
    await queryClient.invalidateQueries({ queryKey: ['job-team'] })
    await queryClient.invalidateQueries({ queryKey: ['qa-team-exclusions', jobId] })
  }

  const search = async (query: string): Promise<QaParticipantPerson[]> => {
    try {
      const response = await client.get(`/jobs/${jobId}/qa-assign/`, { params: { q: query } })
      const data = response.data as { candidates?: unknown } | null
      if (!data || typeof data !== 'object' || !Array.isArray(data.candidates)) {
        throw new Error('Search failed')
      }
      const candidates = data.candidates as Array<{
        user_id?: number
        name?: string | null
        email?: string | null
      }>
      return candidates
        .filter((row): row is { user_id: number; name?: string | null; email?: string | null } =>
          typeof row.user_id === 'number',
        )
        .map((row) => ({
          user_id: row.user_id,
          name: (row.name || row.email || '').trim(),
          email: (row.email || '').trim(),
        }))
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status
      throw new Error(`Search failed: ${status ?? 'error'}`)
    }
  }

  const onAdd = async (person: QaParticipantPerson, role: string) => {
    setBusy(true)
    setError(null)
    try {
      if (onChangePeople) {
        await onChangePeople([
          ...(people ?? []),
          { name: person.name, email: person.email, role, phone: '', userId: person.user_id },
        ])
        onError(null)
        return
      }
      await client.post('/api/qa/participants/', {
        job: jobId,
        user: person.user_id,
        role_on_job: role,
      })
      onError(null)
      await refresh()
    } catch (err) {
      const message = qaErrorMessage(err, 'Could not add the participant.')
      setError(message)
      onError(message)
      throw err
    } finally {
      setBusy(false)
    }
  }

  const onRemove = async (row: QaParticipantRow) => {
    if (row.kind === 'team') {
      const name = row.name || row.email
      if (!window.confirm(`Remove ${name} from this QA? Their Job Hub access stays.`)) return
    }
    setBusy(true)
    setError(null)
    try {
      if (onChangePeople) {
        await onChangePeople((people ?? []).filter((person) => (person.email || person.name) !== (row.email || row.name)))
        onError(null)
        return
      }
      if (row.kind === 'team') {
        await client.post('/api/qa/team-exclusions/', { job: jobId, user: row.userId })
      } else {
        await client.delete(`/api/qa/participants/${row.key}/`)
      }
      onError(null)
      await refresh()
    } catch (err) {
      const message = qaErrorMessage(err, 'Could not remove the participant.')
      setError(message)
      onError(message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <QaParticipantsEditor
      rows={rows}
      search={search}
      onAdd={onAdd}
      onRemove={onRemove}
      busy={busy}
      error={error}
    />
  )
}
